<template>
  <div class="space-y-5 outline-none" tabindex="-1">
    <div>
      <span
        v-if="body.badge"
        class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-primary text-white mb-3"
      >
        <SparklesIcon class="h-3.5 w-3.5" aria-hidden="true" />
        {{ body.badge }}
      </span>
      <h2 class="text-lg font-semibold text-gray-900">{{ body.title }}</h2>
      <p v-if="body.subtitle" class="mt-1 text-sm text-gray-500">{{ body.subtitle }}</p>
    </div>

    <div>
      <SectionRenderer v-for="(block, index) in body.blocks ?? []" :key="index" :section="block" />
    </div>

    <div class="flex flex-col-reverse gap-2 sm:flex-row sm:flex-wrap sm:justify-end">
      <button
        type="button"
        data-test="announcement-dismiss"
        class="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900"
        @click="$emit('dismiss')"
      >
        {{ t("dismiss") }}
      </button>
      <template v-for="cta in ctas" :key="cta.label">
        <a
          v-if="cta.href"
          :href="cta.href"
          target="_blank"
          rel="noopener noreferrer"
          data-test="announcement-cta"
          :class="ctaClass(cta)"
          @click="$emit('dismiss')"
        >
          {{ cta.label }}
        </a>
        <router-link
          v-else
          :to="internalRoute(cta)"
          data-test="announcement-cta"
          :class="ctaClass(cta)"
          @click="$emit('dismiss')"
        >
          {{ cta.label }}
        </router-link>
      </template>
    </div>
  </div>
</template>

<script>
import { useI18n } from "vue-i18n";
import { SparklesIcon } from "@heroicons/vue/outline";
import SectionRenderer from "@/components/training-module/SectionRenderer.vue";

// Generic announcement body: header, training-like blocks and call-to-action links.
// `content` holds one body per locale, falling back to French.
export default {
  components: { SparklesIcon, SectionRenderer },

  props: {
    content: { type: Object, required: true },
    // Viewer role: a call to action with `roles` is only shown to those roles
    role: { type: String, default: null },
  },

  emits: ["dismiss"],

  setup() {
    const { t } = useI18n();
    return { t };
  },

  // Takes the focus itself, so the modal does not preselect (and outline) the first button when it opens
  mounted() {
    this.$el.focus({ preventScroll: true });
  },

  computed: {
    body() {
      return this.content[this.$i18n.locale] ?? this.content.fr ?? {};
    },

    ctas() {
      // A call to action without a valid destination (content typed in by hand) is left out rather than breaking the modal
      return (this.body.ctas ?? []).filter(
        (cta) =>
          (!cta.roles || cta.roles.includes(this.role)) &&
          (cta.href?.startsWith("https://") || this.internalRoute(cta)),
      );
    },
  },

  methods: {
    // An internal link may target each space ({ pos, admin }), so a manager stays in the space they are using.
    internalRoute(cta) {
      const route =
        typeof cta.to === "object" && cta.to !== null
          ? ((this.$route?.path?.startsWith("/pos") ? cta.to.pos : cta.to.admin) ?? cta.to.pos ?? cta.to.admin)
          : cta.to;
      // Only app paths: anything else typed in by hand would lead to a route that does not exist
      return typeof route === "string" && route.startsWith("/") ? route : null;
    },

    ctaClass(cta) {
      return cta.primary
        ? "inline-flex justify-center px-4 py-2 rounded-md text-sm font-medium bg-primary text-white hover:bg-primary-dark"
        : "inline-flex justify-center px-4 py-2 rounded-md text-sm font-medium border border-gray-300 text-gray-700 bg-white hover:bg-gray-50";
    },
  },
};
</script>

<i18n src="./AnnouncementContent.i18n.json"></i18n>
